import {
  formatCurrencyPlain,
  formatPercent,
  formatIntegerPercentInputFromNumber,
} from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

function formatSkuAttributes(attributes) {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return '';
  const entries = Object.entries(attributes).filter(([, value]) => value != null && String(value).trim() !== '');
  if (!entries.length) return '';
  return entries.map(([key, value]) => `${key}: ${value}`).join(' | ');
}

export function resolveBudgetCaptureTitle({
  productName = '',
  skuCode = '',
  skuAttributes = null,
  itemDescription = '',
  simulationDescription = '',
} = {}) {
  const product = String(productName || '').trim();
  const sku = String(skuCode || '').trim();
  const itemDesc = String(itemDescription || '').trim();
  const simDesc = String(simulationDescription || '').trim();
  const skuAttrs = formatSkuAttributes(skuAttributes);

  if (product && sku) {
    return skuAttrs ? `${product} — ${sku} (${skuAttrs})` : `${product} — ${sku}`;
  }
  if (product) return product;
  if (sku) return skuAttrs ? `${sku} (${skuAttrs})` : sku;
  if (itemDesc) return itemDesc;
  return simDesc;
}

export function resolveBudgetCaptureSubtitle({
  productName = '',
  skuCode = '',
  itemDescription = '',
  simulationDescription = '',
} = {}) {
  const title = resolveBudgetCaptureTitle({
    productName,
    skuCode,
    itemDescription,
    simulationDescription: '',
  });
  const itemDesc = String(itemDescription || '').trim();
  const simDesc = String(simulationDescription || '').trim();
  const hasCatalogItem = Boolean(String(productName || '').trim() || String(skuCode || '').trim());

  if (!hasCatalogItem || !itemDesc || itemDesc === title) return '';
  return itemDesc;
}

const fmtMarkupRatio = (x) => {
  if (typeof x !== 'number' || !Number.isFinite(x)) return '—';
  return formatPercent(x);
};

export function slugifyBudgetFilenamePart(text) {
  const base = String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .toLowerCase();
  return base || 'orcamento';
}

export function buildBudgetFilename({ description, format }) {
  const slug = slugifyBudgetFilenamePart(description);
  const date = new Date().toISOString().slice(0, 10);
  const ext = format === 'png' ? 'png' : 'md';
  return `orcamento-${slug}-${date}.${ext}`;
}

export function downloadBudgetFile({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function buildPriceSimulationMarkdown({ translate, constants, payloadProduct, result }) {
  const pk = (k) => translate(`price_simulate.${k}`);
  const money = (v) => formatCurrencyPlain(v);
  const lines = [];

  const heading = (level, title) => {
    lines.push(`${'#'.repeat(level)} ${title}`);
  };
  const blank = () => lines.push('');
  const kv = (label, value) => lines.push(`- ${label}: ${value}`);
  const sep = () => {
    blank();
    lines.push('---');
    blank();
  };

  heading(1, pk('section_detailed'));
  blank();

  heading(2, pk('section_product'));
  if (payloadProduct.description) {
    heading(3, pk('description'));
    lines.push(payloadProduct.description);
    blank();
  }
  kv(pk('quantity'), String(payloadProduct.quantity));
  kv(pk('material'), String(payloadProduct.material));
  kv(pk('print_hours'), String(payloadProduct.print_hours));
  kv(pk('grams'), String(payloadProduct.grams));
  kv(pk('modeling_hours'), String(payloadProduct.modeling_hours));
  kv(pk('painting_hours'), String(payloadProduct.painting_hours));

  if (nf(payloadProduct.selling_price_total) > 0) {
    kv(pk('selling_price'), money(payloadProduct.selling_price_total));
    kv(pk('selling_price_unit'), money(payloadProduct.selling_price_unit));
  }

  let variableCostLines = 0;
  (payloadProduct.variable_costs || []).forEach((row, idx) => {
    const hasAny = String(row.name || '').trim() !== '' || nf(row.cost_unit) > 0;
    if (!hasAny) return;
    if (variableCostLines === 0) {
      heading(3, pk('variable_costs_section'));
    }
    variableCostLines += 1;
    const nm = String(row.name || '').trim() || '—';
    kv(`${idx + 1}. ${nm}`, `${pk('cost_unit')} ${money(row.cost_unit)}`);
  });

  sep();

  heading(2, pk('section_config'));

  heading(3, pk('materials'));
  Object.keys(constants.materials || {})
    .sort()
    .forEach((name) => {
      const row = constants.materials[name];
      kv(`${name} (${pk('cost_per_kg')})`, money(nf(row?.cost_per_kg)));
    });

  heading(3, pk('machine'));
  kv(pk('equipment_maintenance_hour'), money(constants.machine?.equipment_maintenance_per_hour));
  kv(pk('equipment_degradation_hour'), money(constants.machine?.equipment_degradation_per_hour));
  kv(pk('energy_hour'), money(constants.machine?.energy_per_hour));
  kv(pk('print_labor_hour'), money(constants.machine?.print_labor_per_hour));

  heading(3, pk('services_rates'));
  kv(pk('modeling_hour'), money(constants.services?.modeling_per_hour));
  kv(pk('painting_hour'), money(constants.services?.painting_per_hour));

  heading(3, pk('percentages'));
  kv(pk('failure_pct'), `${formatIntegerPercentInputFromNumber(constants.percentages?.failure_percent)}%`);
  kv(pk('marketing_pct'), `${formatIntegerPercentInputFromNumber(constants.percentages?.marketing_percent)}%`);
  kv(
    pk('reinvestment_pct'),
    `${formatIntegerPercentInputFromNumber(constants.pricing?.reinvestment_percent)}%`,
  );
  kv(
    pk('profit_margin_pct'),
    `${formatIntegerPercentInputFromNumber(constants.pricing?.profit_margin_percent)}%`,
  );

  sep();

  heading(2, pk('clipboard_section_breakdown'));

  heading(3, pk('section_direct_cost'));
  lines.push(`↳ ${pk('explain_filament')}`);
  kv(pk('cost_filament'), money(result.costs.filament));
  lines.push(`↳ ${pk('explain_print_machine')}`);
  kv(pk('cost_printing_machine'), money(result.costs.printing_machine));
  (result.costs.variable_costs || []).forEach((line) => {
    if (nf(line.cost_total) <= 0) return;
    kv(`${line.name} (${pk('cost_total')})`, money(line.cost_total));
  });
  kv(pk('subtotal_direct'), money(result.costs.subtotal_direct));
  lines.push(`↳ ${pk('explain_failure')}`);
  kv(pk('cost_failure'), money(result.costs.failure_cost));
  kv(pk('total_direct_cost'), money(result.costs.total_direct_cost));

  heading(3, pk('section_services_billing'));
  lines.push(`↳ ${pk('explain_service_line')}`);
  kv(pk('revenue_modeling'), money(result.services_billing.modeling));
  kv(pk('revenue_painting'), money(result.services_billing.painting));
  kv(pk('revenue_print_monitoring'), money(result.services_billing.print_monitoring));
  kv(pk('services_total'), money(result.services_billing.total));

  heading(3, pk('composition_block'));
  lines.push(`↳ ${pk('explain_composition')}`);
  kv(pk('base_direct_total'), money(result.composition.base_from_direct_cost));
  kv(pk('services_total'), money(result.services_billing.total));
  kv(pk('subtotal_before_marketing'), money(result.composition.subtotal_before_marketing));

  heading(3, pk('marketing_block'));
  lines.push(`↳ ${pk('explain_marketing')}`);
  kv(pk('marketing_pct'), `${formatPercent(result.fees.marketing_percent)}%`);
  kv(pk('price_after_marketing'), money(result.fees.price_after_marketing));

  heading(3, pk('reinvestment_block'));
  lines.push(`↳ ${pk('explain_reinvestment')}`);
  kv(pk('price_before_reinvestment'), money(result.reinvestment.price_before));
  kv(pk('reinvestment_pct_label'), `${formatPercent(result.reinvestment.percent)}%`);
  kv(pk('reinvestment_amount'), money(result.reinvestment.amount));

  heading(3, pk('profit_margin_block'));
  if (result.profit_margin.is_implied) {
    lines.push(`↳ ${pk('explain_implied_profit')}`);
  } else {
    lines.push(`↳ ${pk('explain_profit_margin')}`);
  }
  kv(pk('price_before_profit_margin'), money(result.profit_margin.price_before));
  kv(
    result.profit_margin.is_implied ? pk('implied_profit') : pk('profit_margin_pct_label'),
    `${formatPercent(result.profit_margin.percent)}%`,
  );
  kv(pk('profit_margin_amount'), money(result.profit_margin.amount));

  heading(3, pk('final_price_block'));
  kv(pk('minimum_selling_price'), money(result.pricing.minimum_selling_price ?? result.pricing.final_price));
  kv(
    pk('minimum_unit_price'),
    money(result.pricing.minimum_unit_price ?? result.pricing.unit_price),
  );
  if (nf(result.pricing.selling_price) > 0) {
    kv(pk('selling_price'), money(result.pricing.selling_price));
    kv(pk('selling_price_unit'), money(result.pricing.unit_price));
  } else {
    kv(pk('final_price'), money(result.pricing.final_price));
    kv(pk('unit_price'), money(result.pricing.unit_price));
  }
  kv(pk('effective_markup_direct'), `${fmtMarkupRatio(result.pricing.effective_markup_on_direct)}×`);
  kv(pk('profit_on_direct'), money(result.pricing.profit_on_direct_cost));
  kv(pk('margin_on_final'), `${formatPercent(result.pricing.margin_pct_on_final)}%`);

  return lines.join('\n');
}

export function buildPriceSimulationHistoryNote({ translate, payloadProduct, result }) {
  const money = (v) => formatCurrencyPlain(v);
  const prefix = translate('extensions.price_simulator.history_note_prefix');
  const lines = [prefix];

  const desc = String(payloadProduct?.description || '').trim();
  if (desc) {
    lines.push(desc);
  }

  const directCost = money(result.costs.total_direct_cost);
  const services = money(result.services_billing.total);
  const effectivePrice = money(result.pricing.effective_selling_price ?? result.pricing.final_price);
  const minimumPrice = money(result.pricing.minimum_selling_price ?? result.pricing.final_price);
  const q = Math.max(1, ni(result.detail?.quantity ?? payloadProduct?.quantity));

  const priceLine = nf(result.pricing.selling_price) > 0
    ? `${translate('price_simulate.selling_price')}: ${effectivePrice} | ${translate('price_simulate.minimum_selling_price')}: ${minimumPrice}`
    : `${translate('price_simulate.minimum_selling_price')}: ${minimumPrice}`;

  lines.push(
    `${translate('price_simulate.total_direct_cost')}: ${directCost} | ${translate('price_simulate.services_total')}: ${services} | ${priceLine}`,
  );

  if (q > 1) {
    const unitLabel = nf(result.pricing.selling_price) > 0
      ? translate('price_simulate.selling_price_unit')
      : translate('price_simulate.unit_price');
    lines.push(`${unitLabel}: ${money(result.pricing.unit_price)}`);
  }

  return lines.join('\n');
}

export async function prepareOrderHistoryBudgetAssets({
  translate,
  payloadProduct,
  result,
  captureElement,
}) {
  const note = buildPriceSimulationHistoryNote({ translate, payloadProduct, result });
  let pngBlob = null;
  if (captureElement) {
    pngBlob = await captureElementToPngBlob(captureElement);
  }
  return { note, pngBlob };
}

export async function captureElementToPngBlob(element) {
  if (!element) {
    throw new Error('capture element required');
  }
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    logging: false,
    useCORS: true,
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('PNG export failed'));
    }, 'image/png');
  });
}

export async function savePriceSimulationBudget({
  disposition,
  format,
  translate,
  constants,
  payloadProduct,
  result,
  captureElement,
  onCommitOrderHistory,
}) {
  if (!result || !constants || !payloadProduct) {
    throw new Error('simulation required');
  }

  if (disposition === 'orderHistory') {
    if (typeof onCommitOrderHistory !== 'function') {
      throw new Error('onCommitOrderHistory required');
    }
    const note = buildPriceSimulationHistoryNote({ translate, payloadProduct, result });
    let pngBlob = null;
    if (captureElement) {
      pngBlob = await captureElementToPngBlob(captureElement);
    }
    await onCommitOrderHistory({ note, pngBlob });
    return;
  }

  if (disposition !== 'download') {
    throw new Error('invalid disposition');
  }

  const filename = buildBudgetFilename({
    description: payloadProduct.description,
    format,
  });

  if (format === 'markdown') {
    const text = buildPriceSimulationMarkdown({ translate, constants, payloadProduct, result });
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    downloadBudgetFile({ blob, filename });
    return;
  }

  if (format === 'png') {
    const blob = await captureElementToPngBlob(captureElement);
    downloadBudgetFile({ blob, filename });
    return;
  }

  throw new Error('invalid format');
}
